import { ScrollingModule } from '@angular/cdk/scrolling';

import { Component, computed, inject, TemplateRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { faker } from '@faker-js/faker';

import { MdwAvatar } from '@spaces-ui/meadow/components/avatar';
import { MdwDialogService } from '@spaces-ui/meadow/components/dialog';
import { MdwTable } from '@spaces-ui/meadow/components/table';
import { TableConfig, TableSort } from '@spaces-ui/meadow/components/table/models';
import { Delete_Outline, Edit_Outline, Group_Fill } from '@spaces-ui/meadow/icons/svgs';
import { alphaSortByProperty } from '@spaces-ui/utils';

import { Sample } from '../../components';

interface TableItem {
  id: string;
  name: string;
  jobTitle: string;
  birthdate: string;
  avatarColor: number;
}

interface DocsTableItem {
  property: string;
  type: string;
  description: string;
}

@Component({
  selector: 'app-tables',
  templateUrl: './tables.html',
  styleUrls: ['./tables.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MdwTable, Sample, MdwAvatar, ScrollingModule],
})
export class Tables {
  private dialogService = inject(MdwDialogService);

  public tableConfigDocs: TableConfig<DocsTableItem> = {
    items: [
      {
        property: 'items',
        type: 'T[]',
        description: 'This is the list of data that will be used to display table rows',
      },
      {
        property: 'columns',
        type: 'ColumnConfig[]',
        description: 'This is the configuration for the columns in the table',
      },
      {
        property: 'sortProperty',
        type: 'keyof T',
        description: `**OPTIONAL** This is the property that is currently being used to sort. NOTE: this
          component doesn't do the sorting for you, this property is used for display purposes only`,
      },
      {
        property: 'sortDirection',
        type: `'asc' | 'desc'`,
        description: `**OPTIONAL** This is the direction that the sortProperty is currently being sorted by.
          NOTE: this component doesn't do the sorting for you, this property is used for display
          purposes only`,
      },
      {
        property: 'hideActions',
        type: 'boolean',
        description: `**OPTIONAL** This will hide the actions button`,
      },
      {
        property: 'showHeader',
        type: 'boolean',
        description: `**OPTIONAL** This will show the header row`,
      },
      {
        property: 'selectable',
        type: 'boolean',
        description: `**OPTIONAL** When true, prepends a checkbox column for row selection and adds a
          select-all checkbox to the header. Requires \`trackBy\` to uniquely identify rows. Listen
          to (selectionChange) for the current selection`,
      },
      {
        property: 'selectionActions',
        type: 'SelectionAction[]',
        description: `**OPTIONAL** Bulk actions shown in a toolbar that replaces the column headers
          while rows are selected. Emitted via (handleSelectionAction) (see example below)`,
      },
      {
        property: 'itemLabel',
        type: '{ singular: string; plural: string }',
        description: `**OPTIONAL** The noun the table uses to refer to its rows (e.g. the selection
          toolbar shows "3 teammates selected"). The singular label is used when exactly one row is
          selected, otherwise the plural label. Falls back to "item"/"items" when omitted`,
      },
      {
        property: 'trackBy',
        type: '(index: number, item: T) => string',
        description: `**OPTIONAL** This is the function that will be used to track the items in the list. If
          this is not supplied, the table will track by index`,
      },
      {
        property: 'zeroState',
        type: 'ZeroStateConfig',
        description: `**OPTIONAL** This is the configuration for the zero state. If not supplied, table will show as blank when no items are rendered`,
      },
      {
        property: 'searchText',
        type: 'string',
        description: `**OPTIONAL** This is the text that will be displayed when a search is active. NOTE this component doesn't do the search for you, this is only used for display purposes`,
      },
      {
        property: 'itemClick',
        type: '(item: T) => void',
        description: `**OPTIONAL** This is the function that will be called when an item is clicked. If not supplied, clicking an item will do nothing`,
      },
    ],
    columns: [
      { name: 'property', label: 'Property', allowCellTextWrap: true, width: '20%' },
      { name: 'type', label: 'Type', allowCellTextWrap: true, width: '20%' },
      { name: 'description', label: 'Description', allowCellTextWrap: true },
    ],
    showHeader: true,
    trackBy: (index, item) => item.property,
  };

  public columnConfigDocs: TableConfig<DocsTableItem> = {
    items: [
      {
        property: 'name',
        type: 'keyof T',
        description: `**OPTIONAL** This is the property on the item that will be used to display the data in
          this column. NOTE this is required for all columns unless actions are defined`,
      },
      {
        property: 'label',
        type: 'string',
        description: `**OPTIONAL** this is the text that will be displayed in the header cell. NOTE this is
          required if you want the header cell to have text content`,
      },
      {
        property: 'sortable',
        type: 'boolean',
        description: `**OPTIONAL** whether or not to enable sorting on this column`,
      },
      {
        property: 'width',
        type: 'string',
        description: `**OPTIONAL** the width to use for this column. If not defined, it defaults to auto`,
      },
      {
        property: 'allowCellTextWrap',
        type: 'boolean',
        description: `**OPTIONAL** whether or not to allow text to wrap in this column. If not defined, text will be truncated`,
      },
      {
        property: 'customTemplate',
        type: 'TemplateRef',
        description: `**OPTIONAL** This is a template that will be used to render the cell. If this is defined,
          each cell in this column will use this template to render the cell instead of the plain
          text cell (see example below)`,
      },
      {
        property: 'actions',
        type: 'ActionConfig',
        description: `**OPTIONAL** defines the actions to show in a flyout menu (see example below)`,
      },
      {
        property: 'cellClass',
        type: '(item: T) => string',
        description: `**OPTIONAL** A function that returns a CSS class name to apply to the cell. This is called for each row, allowing dynamic styling per item (e.g. highlighting cells with validation errors)`,
      },
      {
        property: 'cellTooltip',
        type: '(item: T) => string',
        description: `**OPTIONAL** A function that returns tooltip text to show on hover for the cell. Only applies to editable cells. When it returns a non-empty string, that text is shown; otherwise the cell falls back to the default "Double-click to edit" hint. Useful for surfacing per-row context such as validation error messages`,
      },
    ],
    columns: [
      { name: 'property', label: 'Property', allowCellTextWrap: true, width: '20%' },
      { name: 'type', label: 'Type', allowCellTextWrap: true, width: '20%' },
      { name: 'description', label: 'Description', allowCellTextWrap: true },
    ],
    showHeader: true,
    trackBy: (index, item) => item.property,
  };

  public actionConfigDocs: TableConfig<DocsTableItem> = {
    items: [
      {
        property: 'name',
        type: 'string',
        description: `This is the name of the action. This will be emitted when the action is
          clicked`,
      },
      {
        property: 'label',
        type: 'string | ((item: T) => string)',
        description: `This is the label to be displayed in the flyout. You can pass a function in
          if you need different items in the table to have different text values for this action`,
      },
      {
        property: 'icon',
        type: 'string | ((item: T) => string)',
        description: `**OPTIONAL** This is the icon that will be displayed next to the action label. You can
          pass a function in if you need different items in the table to have different icons for
          this action`,
      },
      {
        property: 'disabled',
        type: 'boolean | ((item: T) => boolean)',
        description: `**OPTIONAL** Whether or not this action should be disabled. You can pass a function in
          if you need different items in the table to have different disabled states for this action.
          If not defined, the action will always be enabled`,
      },
      {
        property: 'disabledTooltip',
        type: 'string | ((item: T) => string)',
        description: `**OPTIONAL** The tooltip to display when the action is disabled. You can pass a function in
          if you need different items in the table to have different tooltips for this action`,
      },
    ],
    columns: [
      { name: 'property', label: 'Property', allowCellTextWrap: true, width: '20%' },
      { name: 'type', label: 'Type', allowCellTextWrap: true, width: '20%' },
      { name: 'description', label: 'Description', allowCellTextWrap: true },
    ],
    showHeader: true,
    trackBy: (index, item) => item.property,
  };

  public selectionActionConfigDocs: TableConfig<DocsTableItem> = {
    items: [
      {
        property: 'name',
        type: 'string',
        description: `This is the name of the action. It is emitted via (handleSelectionAction) when
          the action is clicked`,
      },
      {
        property: 'label',
        type: 'string | ((items: T[]) => string)',
        description: `The label displayed on the action button. Pass a function in if you need the
          label to vary based on the currently selected items`,
      },
      {
        property: 'icon',
        type: 'string | ((items: T[]) => string)',
        description: `**OPTIONAL** An icon displayed on the action button. Pass a function in if you
          need the icon to vary based on the currently selected items`,
      },
      {
        property: 'isVisible',
        type: 'boolean | ((items: T[]) => boolean)',
        description: `**OPTIONAL** Whether the action is shown. Defaults to true. Pass a function in to
          show/hide the action based on the selected items (e.g. only show "Merge" when 2+ rows are
          selected)`,
      },
      {
        property: 'disabled',
        type: 'boolean | ((items: T[]) => boolean)',
        description: `**OPTIONAL** Whether the action is disabled. Pass a function in if you need the
          disabled state to vary based on the selected items`,
      },
      {
        property: 'disabledTooltip',
        type: 'string | ((items: T[]) => string)',
        description: `**OPTIONAL** The tooltip to display when the action is disabled`,
      },
    ],
    columns: [
      { name: 'property', label: 'Property', allowCellTextWrap: true, width: '20%' },
      { name: 'type', label: 'Type', allowCellTextWrap: true, width: '20%' },
      { name: 'description', label: 'Description', allowCellTextWrap: true },
    ],
    showHeader: true,
    trackBy: (index, item) => item.property,
  };

  public zeroStateConfigDocs: TableConfig<DocsTableItem> = {
    items: [
      {
        property: 'title',
        type: 'string',
        description: `This is the label to be used when there are no items in the table and no active search`,
      },
      {
        property: 'icon',
        type: 'string',
        description: `**OPTIONAL** This is the icon to be displayed in the zero state`,
      },
      {
        property: 'description',
        type: 'string',
        description: `**OPTIONAL** This is a secondary description to be displayed below the title`,
      },
    ],
    columns: [
      { name: 'property', label: 'Property', allowCellTextWrap: true, width: '20%' },
      { name: 'type', label: 'Type', allowCellTextWrap: true, width: '20%' },
      { name: 'description', label: 'Description', allowCellTextWrap: true },
    ],
    showHeader: true,
    trackBy: (index, item) => item.property,
  };

  //#region sample:noItemsTable
  public noItemsTableConfig: TableConfig<TableItem> = {
    items: [],
    columns: [
      { name: 'name', label: 'Name', width: '60%' },
      { name: 'jobTitle', label: 'Job Title' },
      { name: 'birthdate', label: 'Birthdate' },
    ],
    zeroState: {
      title: 'No teammates yet',
      icon: Group_Fill,
      description: 'get conversations started by adding teammates',
    },
    showHeader: true,
    trackBy: (index, item) => item.id,
  };
  //#endregion

  //#region sample:noSearchItemsTable
  public noSearchItemsTableConfig: TableConfig<TableItem> = {
    items: [],
    columns: [
      { name: 'name', label: 'Name', width: '60%' },
      { name: 'jobTitle', label: 'Job Title' },
      { name: 'birthdate', label: 'Birthdate' },
    ],
    zeroState: {
      title: 'No teammates yet',
      icon: Group_Fill,
      description: 'get conversations started by adding teammates',
    },
    showHeader: true,
    searchText: 'test user name',
    trackBy: (index, item) => item.id,
  };
  //#endregion

  //#region sample:simpleTable
  public simpleTableConfig: TableConfig<TableItem> = {
    items: this.generateItems(20),
    columns: [
      { name: 'name', label: 'Name', width: '60%' },
      { name: 'jobTitle', label: 'Job Title' },
      { name: 'birthdate', label: 'Birthdate' },
    ],
    showHeader: true,
    trackBy: (index, item) => item.id,
    itemClick: (event: MouseEvent, item: TableItem) =>
      this.dialogService.alert(`You clicked on ${item.name}`),
  };
  //#endregion

  //#region sample:virtualTable
  public virtualTableConfig: TableConfig<TableItem> = {
    items: this.generateItems(10000),
    columns: [
      { name: 'name', label: 'Name', width: '60%' },
      { name: 'jobTitle', label: 'Job Title' },
      { name: 'birthdate', label: 'Birthdate' },
    ],
    showHeader: true,
    trackBy: (index, item) => item.id,
    itemClick: (event: MouseEvent, item: TableItem) =>
      this.dialogService.alert(`You clicked on ${item.name}`),
  };
  //#endregion

  //#region sample:actionsTable
  public actionsTableConfig: TableConfig<TableItem> = {
    items: this.generateItems(10000),
    columns: [
      { name: 'name', label: 'Name' },
      { name: 'jobTitle', label: 'Job Title' },
      { name: 'birthdate', label: 'Birthdate' },
      {
        actions: [
          { name: 'edit', icon: Edit_Outline, label: 'Edit' },
          { name: 'delete', icon: Delete_Outline, label: 'Delete' },
        ],
        width: '46px',
      },
    ],
    showHeader: true,
    trackBy: (index, item) => item.id,
  };
  //#endregion

  //#region sample:selectionTable
  public selectionTableConfig: TableConfig<TableItem> = {
    items: this.generateItems(10000),
    columns: [
      { name: 'name', label: 'Name' },
      { name: 'jobTitle', label: 'Job Title' },
      { name: 'birthdate', label: 'Birthdate' },
    ],
    selectable: true,
    selectionActions: [
      { name: 'merge', icon: Group_Fill, label: 'Merge', isVisible: items => items.length >= 2 },
      { name: 'export', label: 'Export' },
      { name: 'delete', icon: Delete_Outline, label: 'Delete' },
    ],
    itemLabel: { singular: 'user', plural: 'users' },
    showHeader: true,
    trackBy: (index, item) => item.id,
  };

  public onSelectionChange(items: TableItem[]): void {
    console.log('Selection changed:', items);
  }

  public onSelectionAction(event: { action: string; selectedItems: TableItem[] }): void {
    this.dialogService.alert(
      `Action "${event.action}" on ${event.selectedItems.length} item(s)`,
    );
  }
  //#endregion

  //#region sample:customRenderer
  public renderer = viewChild('userRenderer', { read: TemplateRef });
  public customRendererTableConfig = computed(() => {
    const renderer = this.renderer();

    const config: TableConfig<TableItem> = {
      items: this.generateItems(10000),
      columns: [
        { name: 'name', label: 'Name', customTemplate: renderer },
        { name: 'jobTitle', label: 'Job Title' },
        { name: 'birthdate', label: 'Birthdate' },
        {
          actions: [
            { name: 'edit', icon: Edit_Outline, label: 'Edit' },
            { name: 'delete', icon: Delete_Outline, label: 'Delete' },
          ],
          width: '46px',
        },
      ],
      showHeader: true,
      trackBy: (index, item) => item.id,
    };

    return config;
  });
  //#endregion

  //#region sample:sortTable
  public sortableTableConfig: TableConfig<TableItem> = {
    items: this.generateItems(10000),
    sortProperty: 'name',
    sortDirection: 'asc',
    columns: [
      { name: 'name', label: 'Name', sortable: true, width: '60%' },
      { name: 'jobTitle', label: 'Job Title', sortable: true },
      { name: 'birthdate', label: 'Birthdate' },
    ],
    showHeader: true,
    trackBy: (index, item) => item.id,
  };

  public handleSort(sort: TableSort<TableItem>): void {
    this.sortableTableConfig = {
      ...this.sortableTableConfig,
      sortProperty: sort.sortProperty,
      sortDirection: sort.sortDirection,
      items: alphaSortByProperty(
        this.sortableTableConfig.items,
        sort.sortProperty,
        sort.sortDirection === 'desc',
      ),
    };
  }
  //#endregion

  //#region sample:actionsTable
  public handleAction(action: { action: string; item: TableItem }): void {
    alert(`Action: ${action.action} on item: ${action.item.name}`);
  }
  //#endregion

  private generateItems(count: number): TableItem[] {
    const items: TableItem[] = [];

    for (let i = 0; i < count; i++) {
      items.push({
        id: faker.string.uuid(),
        name: faker.person.fullName(),
        jobTitle: faker.person.jobTitle(),
        birthdate: faker.date.past({ years: 23 }).toLocaleDateString(),
        avatarColor: faker.number.int({ min: 0, max: 8 }),
      });
    }

    return items;
  }
}
